/**
 * StyledComponents.tsx - Shared Styled Components Library
 *
 * This file contains reusable styled components used throughout the application
 * to ensure consistent theming, spacing, and visual design patterns.
 *
 * Key Features:
 * - Consistent button styles and variants
 * - Modal components with standard layouts
 * - Form elements with unified styling
 * - Layout components for consistent spacing
 * - Theme-aware color and typography systems
 *
 * Component Categories:
 * - Buttons: Primary, secondary, danger variants
 * - Modals: Overlay, content, headers, footers
 * - Forms: Inputs, labels, validation states
 * - Layout: Containers, grids, spacing utilities
 * - Typography: Headings, text, emphasis styles
 *
 * Usage:
 * - Import specific components as needed
 * - Consistent props interface across components
 * - Theme integration for colors and spacing
 * - Responsive design built into components
 *
 * Dependencies: Styled-components, theme system
 */

import { SubscriptionStatus } from "@mindmemobile/mm-payment-modules";
import styled from "styled-components";

// ===== MODAL COMPONENTS =====
export const ModalOverlay = styled.div<{ visible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: ${(props) => (props.visible ? "flex" : "none")};
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  backdrop-filter: blur(4px);
`;

export const ModalContent = styled.div`
  background: #ffffff;
  border-radius: 24px;
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

export const WideModalContent = styled.div`
  background: #ffffff;
  border-radius: 24px;
  width: 100%;
  max-width: 690px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

export const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 32px 32px 24px;
  border-bottom: 1px solid #f1f5f9;
`;

export const ModalTitle = styled.h3`
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
`;

export const CloseButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: rgba(0, 0, 0, 0.1);
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.2);
    color: #1e293b;
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }
`;

// ===== FORM COMPONENTS =====
export const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 20px;
`;

export const FormLabel = styled.label`
  font-size: 16px;
  font-weight: 600;
  color: #374151;
`;

export const FormSection = styled.div`
  margin-bottom: 20px;
`;

export const FormInput = styled.input<{ hasError?: boolean }>`
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 16px;
  color: #1e293b;
  transition: border-color 0.2s ease;
  border-color: ${({ hasError }) =>
    hasError ? "#ef4444" : "#e2e8f0"}; // red if error

  &:focus {
    outline: none;
    border-color: #10b981;
    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

// ===== SEARCH COMPONENTS =====
export const SearchWrapper = styled.div`
  position: relative;
  width: 300px;

  @media (max-width: 640px) {
    width: 100%;
  }
`;

export const SearchInput = styled.input`
  width: 100%;
  padding: 14px 20px 14px 48px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 16px;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(8px);
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2364748b'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: 16px center;
  background-size: 20px;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #10b981;
    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
  }
`;

export const ClearButton = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #64748b;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s ease;

  &:hover {
    color: #1e293b;
    background: #f1f5f9;
  }
`;

// ===== BUTTON COMPONENTS =====
export const PrimaryButton = styled.button<{ disabled?: boolean }>`
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  transition: all 0.2s ease;
  border: none;
  background: ${(props) => (props.disabled ? "#9ca3af" : "#10b981")};
  color: #ffffff;
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};

  &:hover:not(:disabled) {
    background: #059669;
  }
`;

export const SecondaryButton = styled.button`
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  background: #f1f5f9;
  color: #64748b;

  &:hover {
    background: #e2e8f0;
  }
`;

// ===== TABLE COMPONENTS =====
export const DataTable = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(12px);
`;

export const TableHeader = styled.div<{ columns: string }>`
  display: grid;
  grid-template-columns: ${(props) => props.columns};
  padding: 20px 24px;
  background: linear-gradient(135deg, #f8fafc, #e2e8f0);
  border-bottom: 1px solid #e2e8f0;
  font-size: 14px;
  font-weight: 700;
  color: #374151;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export const TableRow = styled.div<{ columns: string }>`
  display: grid;
  grid-template-columns: ${(props) => props.columns};
  padding: 20px 24px;
  border-bottom: 1px solid #f1f5f9;
  transition: all 0.2s ease;
  align-items: center;

  &:hover {
    background: linear-gradient(135deg, #f8fafc, #ffffff);
  }

  &:last-child {
    border-bottom: none;
  }
`;

// ===== CARD COMPONENTS =====
export const Card = styled.div`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  padding: 32px;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.3);
`;

export const Section = styled.div`
  background: #ffffff;
  border-radius: 16px;
  padding: 24px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
`;

// ===== STATUS COMPONENTS =====
export const StatusBadge = styled.div<{ status: keyof typeof SubscriptionStatus }>`
  display: inline-block;
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  ${(props) => {
    switch (props.status) {
      case "Active":
        return "background: #dcfce7; color: #166534;";
      case "Completed":
        return "background: #dbeafe; color: #1e40af;";
      case "Inactive":
        return "background: #fef3c7; color: #92400e;";
      case "Pending":
        return "background: #f3f4f6; color: #374151;";
      default:
        return "background: #f3f4f6; color: #374151;";
    }
  }}
`;

// ===== UTILITY COMPONENTS =====
export const NoResults = styled.div`
  padding: 40px;
  text-align: center;
  color: #64748b;
  font-size: 16px;
`;

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 80px 40px;
  min-height: 400px;
`;

export const EmptyStateIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  color: #94a3b8;
`;

export const EmptyStateTitle = styled.h4`
  font-size: 22px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 8px 0;
`;

export const EmptyStateText = styled.p`
  font-size: 14px;
  color: #64748b;
  margin: 0;
  line-height: 1.5;
`;

// ===== SEGMENT CONTROL COMPONENTS =====
export const SegmentControl = styled.div`
  display: inline-flex;
  background: #f1f5f9;
  border-radius: 12px;
  padding: 4px;
  border: 1px solid #e2e8f0;
`;

export const SegmentButton = styled.button<{ active: boolean }>`
  padding: 10px 20px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  ${(props) =>
    props.active
      ? `
    background: #ffffff;
    color: #1e293b;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  `
      : `
    background: transparent;
    color: #64748b;
    
    &:hover {
      color: #1e293b;
    }
  `}
`;

// Donations Tab Segment Control - Light gray background with white non-selected tabs
export const DonationsSegmentControl = styled.div`
  background: #f3f4f6;
  backdrop-filter: blur(16px);
  border-radius: 16px;
  padding: 6px;
  display: flex;
  gap: 6px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
`;

export const DonationsSegmentButton = styled.button<{ active: boolean }>`
  padding: 10px 18px;
  border: none;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  min-width: 100px;

  ${(props) =>
    props.active
      ? `
    background: #1e293b;
    color: #ffffff;
    box-shadow: 0 2px 8px rgba(30, 41, 59, 0.3);
    transform: translateY(-1px);
  `
      : `
    background: #ffffff;
    color: #64748b;
    
    &:hover {
      background: rgba(30, 41, 59, 0.08);
      color: #1e293b;
    }
  `}
`;
